import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { buttonVariants } from "./button-variants";

/**
 * Button component with WCAG accessibility improvements:
 * - aria-busy when loading
 * - aria-disabled for disabled state
 * - Loading spinner has aria-hidden and sr-only loading text
 */
const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      children,
      disabled,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isIconButton =
      size === "icon" ||
      size === "icon-sm" ||
      size === "icon-lg" ||
      size === "icon-xl" ||
      size === "mobile-icon";

    // Warn in development if icon button lacks aria-label
    if (import.meta.env.DEV && isIconButton && !ariaLabel) {
      console.warn(
        "Button: Icon buttons should have an aria-label for accessibility.",
        { children },
      );
    }

    // When asChild is true, use Slot and only pass through children
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={disabled || loading}
          aria-disabled={disabled || loading ? "true" : undefined}
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    const defaultLoadingText = t("common.loading", "Loading...");

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-disabled={disabled || loading ? "true" : undefined}
        aria-busy={loading ? "true" : undefined}
        aria-label={ariaLabel}
        {...props}
      >
        {loading && (
          <>
            <Loader2 className="animate-spin" aria-hidden="true" />
            <span className="sr-only">{loadingText || defaultLoadingText}</span>
          </>
        )}
        {!loading && children}
      </button>
    );
  },
);
Button.displayName = "Button";

/**
 * Floating Action Button - mobile-friendly primary action button
 * Requires aria-label for accessibility
 */
const FloatingActionButton = React.forwardRef(
  (
    {
      className,
      variant = "default",
      size = "default",
      extended = false,
      children,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    // Warn in development if FAB lacks aria-label
    if (import.meta.env.DEV && !ariaLabel && !extended) {
      console.warn(
        "FloatingActionButton: Should have an aria-label for accessibility.",
        { children },
      );
    }

    return (
      <button
        ref={ref}
        className={cn(
          "fab",
          extended && "fab-extended",
          size === "sm" && "fab-mini",
          variant === "secondary" && "bg-secondary text-secondary-foreground",
          variant === "destructive" &&
            "bg-destructive text-destructive-foreground",
          className,
        )}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </button>
    );
  },
);
FloatingActionButton.displayName = "FloatingActionButton";

/**
 * IconButton - touch-friendly icon button that meets 44px minimum
 * Requires aria-label for accessibility
 */
const IconButton = React.forwardRef(
  (
    {
      className,
      variant = "ghost",
      size = "icon",
      children,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    if (import.meta.env.DEV && !ariaLabel) {
      console.warn("IconButton: Should have an aria-label for accessibility.", {
        children,
      });
    }

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        aria-label={ariaLabel}
        className={cn(
          // Ensure 44px minimum on touch devices
          "touch-action-manipulation",
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    );
  },
);
IconButton.displayName = "IconButton";

export { Button, FloatingActionButton, IconButton };
